import {TailSpin} from "react-loader-spinner";
import React, {useCallback, useEffect, useState} from "react";
import Stat from "./Stat";
import ReportCard from "./ReportCard";
import {CompaniesTableFilter, CompaniesTableRow, TableHeader, TablePagination} from "../table";
import {Table, TableBody, TableContainer} from "@mui/material";
import _ from "lodash";
import {companiesSummaryTableHead} from "../../app/config/tables/companiesTableHead";
import CompanyService from "../services/CompanyService";

const defaultFilter = {
    search: '',
    sector: null,
    openComplainsAmountFrom: null,
    openComplainsAmountTo: null,
    closeComplainsAmountFrom: null,
    closeComplainsAmountTo: null,
    orderBy: 'id',
    orderDirection: 'desc',
    pageNumber: 1,
    pageSize: 6,
};

const CompaniesTab = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState(0);
    const [open, setOpen] = useState(null);
    const [companiesList, setCompaniesList] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [filter, setFilter] = useState(defaultFilter);
    const [pagination, setPagination] = useState({ hasNext: false, hasPrevious: false });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await CompanyService.getList(filter);
            setCompaniesList(response.data.items);
            setPagination({
                hasNext: response.data.hasNext,
                hasPrevious: response.data.hasPrevious,
            });
        } catch (error) {
            console.error("Error fetching companies:", error);
        } finally {
            setIsLoading(false);
        }
    }, [filter]);

    const clearFilters = () => {
        setFilter(defaultFilter);
        setActiveFilter(0);
    }

    const onMostClick = (period) => {
        let newFilter;
        switch (period) {
            case 'month':
                setActiveFilter(1);
                newFilter = { ...filter, orderBy: "mostIn30Days", orderDirection: "desc", pageNumber: 1 };
                break;
            case 'year':
                setActiveFilter(2);
                newFilter = { ...filter, orderBy: "mostIn365Days", orderDirection: "desc", pageNumber: 1 };
                break;
            default:
                newFilter = { ...filter };
        }
        setFilter(newFilter);
    }

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilter(prevFilter => ({...prevFilter, [field]: value, pageNumber: 1}));
    }

    const handleRequestSort = (event, property) => {
        const direction = filter.orderBy === property && filter.orderDirection === "asc" ? "desc" : "asc";
        setFilter(prevFilter => ({ ...prevFilter, orderBy: property, orderDirection: direction, pageNumber: 1 }));
    }

    const handleRowCheck = (item, isChecked) => {
        if (!isChecked) {
            setSelectedItems(prev => [...prev, item]);
        } else {
            setSelectedItems(prev => prev.filter(selected => selected.id !== item.id));
        }
    }

    const handleSelectAll = () => {
        if (selectedItems.length === companiesList.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems([...companiesList]);
        }
    }

    const handleRowClick = (id) => {
        setOpen(prev => prev === id ? null : id);
    }

    const handlePreviousPage = () => {
        if (pagination.hasPrevious) {
            setFilter(prev => ({...prev, pageNumber: prev.pageNumber - 1}));
        }
    }

    const handleNextPage = () => {
        if (pagination.hasNext) {
            setFilter(prev => ({...prev, pageNumber: prev.pageNumber + 1}));
        }
    }

    if(isLoading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100vw', height: '80vh'}}>
                <TailSpin
                    visible={true}
                    height="80"
                    width="80"
                    color="#4fa94d"
                    ariaLabel="tail-spin-loading"
                    radius="1"
                    wrapperStyle={{}}
                    wrapperClass=""
                />
            </div>
        )
    }


    return (
        <div className="fk-screen">
            <div style={{display: 'flex', flexDirection: 'row'}}>
                <span className="rowHeaderText">Quick filters </span>
                <span style={{color: '#B2B2B2', marginLeft: 12, cursor: 'pointer'}}
                      onClick={clearFilters}>Clear filters</span>
            </div>

            <div className="rowContainer">
                <ReportCard label={'Cases created'} isLoading={isLoading}>
                    <Stat value={'Most'}
                          description={"this month"}
                          onClick={() => onMostClick('month')}
                          period={'month'}
                          filterNumber={1}
                          activeFilter={activeFilter}
                    />

                    <Stat value={'Most'}
                          description={"this year"}
                          onClick={() => onMostClick('year')}
                          period={'year'}
                          filterNumber={2}
                          activeFilter={activeFilter}
                    />
                </ReportCard>
            </div>


            <CompaniesTableFilter filter={filter} setFilter={handleFilterChange} />
            <TableContainer>
                <Table size="medium" className="fk-table">
                    <TableHeader
                        crud
                        withCheckbox
                        items={companiesSummaryTableHead}
                        orderBy={filter.orderBy}
                        order={filter.orderDirection}
                        numSelected={selectedItems.length}
                        rowCount={companiesList.length}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAll}
                        screen="CompaniesScreen"
                    />

                    <TableBody>
                        {companiesList.map((company, index) => {
                            const isChecked = _.some(selectedItems, (item) => item.id === company.id);

                            return (
                                <CompaniesTableRow
                                    key={`${company.id}+${index}`}
                                    checked={isChecked}
                                    company={company}
                                    open={open === company.id}
                                    onRowClick={handleRowClick}
                                    onCheck={() => handleRowCheck(company, isChecked)}
                                />
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                filter={{...filter, ...pagination}}
                handlePreviousPage={handlePreviousPage}
                handleNextPage={handleNextPage}
            />


        </div>
    )
}

export default CompaniesTab;